import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

// Semantic status colours — deliberately separate from any brand accent, so
// "needs attention" reads as urgency rather than decoration.
const STATUS = {
  ok:       { bg: '#e8f5e9', fg: '#1e7d43', dot: '#2e7d5b', label: 'Healthy' },
  warn:     { bg: '#fff8e1', fg: '#8a6100', dot: '#c9910d', label: 'Attention' },
  critical: { bg: '#fdecea', fg: '#b3271c', dot: '#c0392b', label: 'Action needed' },
  unknown:  { bg: '#f1f3f5', fg: '#5b6472', dot: '#95a2b3', label: 'Unknown' },
};
const statusOf = (s) => STATUS[s] || STATUS.unknown;

const card = {
  background: '#fff',
  border: '1px solid #e8ecef',
  borderRadius: '8px',
  padding: '14px 16px',
};

const pill = (s) => ({
  background: statusOf(s).bg,
  color: statusOf(s).fg,
  borderRadius: '10px',
  padding: '3px 10px',
  fontSize: '11px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
});

const btn = (bg) => ({
  padding: '7px 14px',
  background: bg,
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '13px',
});

function Dot({ status }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        background: statusOf(status).dot,
        flexShrink: 0,
      }}
    />
  );
}

// A certificate's remaining life is the number people actually act on, so it
// gets its own emphasised line rather than being buried in the detail text.
function DaysRemaining({ days }) {
  if (days === null || days === undefined) return null;
  return (
    <div style={{ fontSize: '26px', fontWeight: 600, color: '#2c3e50', lineHeight: 1.1 }}>
      {days}
      <span style={{ fontSize: '13px', fontWeight: 400, color: '#7f8c8d', marginLeft: '5px' }}>
        {Math.abs(days) === 1 ? 'day left' : 'days left'}
      </span>
    </div>
  );
}

function CheckCard({ check }) {
  const s = statusOf(check.status);
  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Dot status={check.status} />
        <span style={{ fontWeight: 600, color: '#2c3e50', fontSize: '13px', flex: 1 }}>
          {check.label}
        </span>
        <span style={pill(check.status)}>{s.label}</span>
      </div>

      <DaysRemaining days={check.days_remaining} />

      <div style={{ fontSize: '12.5px', color: '#5b6472', lineHeight: 1.45 }}>
        {check.detail}
      </div>

      {(check.expires_at || check.issuer) && (
        <div style={{ fontSize: '11.5px', color: '#95a2b3' }}>
          {check.expires_at && `Expires ${new Date(check.expires_at).toLocaleDateString()}`}
          {check.expires_at && check.issuer && ' · '}
          {check.issuer && `Issued by ${check.issuer}`}
        </div>
      )}

      {check.db_revision && (
        <div style={{ fontSize: '11.5px', color: '#95a2b3' }}>
          Database {check.db_revision}
          {check.code_revision && ` · code ships ${check.code_revision}`}
        </div>
      )}
    </div>
  );
}

// UptimeRobot watches from OUTSIDE, which is the one thing this page structurally
// cannot do for itself: if the API were down, this page would be down with it. Shown
// alongside our own checks so there's one place to look — but never folded into the
// page's overall verdict, since UptimeRobot being unreachable says nothing about
// whether NeTrA is healthy.
function ExternalUptime({ data }) {
  if (!data) return null;

  if (!data.configured) {
    return (
      <div style={{ ...card, color: '#5b6472', fontSize: '12.5px' }}>
        <div style={{ fontWeight: 600, color: '#2c3e50', marginBottom: '4px', fontSize: '13px' }}>
          External uptime isn't connected
        </div>
        {data.detail} An admin can add one — see the System Health runbook.
      </div>
    );
  }

  if (!data.monitors?.length) {
    return (
      <div style={{ ...card, color: '#5b6472', fontSize: '12.5px' }}>{data.detail}</div>
    );
  }

  const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`);

  return (
    <div style={{ ...card, overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '12.5px' }}>
        <thead>
          <tr>
            {['Monitor', 'Now', '24 hours', '7 days', '30 days'].map((h, i) => (
              <th
                key={h}
                style={{
                  textAlign: i === 0 ? 'left' : 'right',
                  padding: '6px 10px',
                  color: '#7f8c8d',
                  fontWeight: 600,
                  fontSize: '11.5px',
                  borderBottom: '2px solid #e8ecef',
                  whiteSpace: 'nowrap',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.monitors.map((m) => (
            <tr key={m.name}>
              <td style={{ padding: '7px 10px', borderBottom: '1px solid #f0f2f4' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                  <Dot status={m.is_up ? 'ok' : 'critical'} />
                  {m.name}
                </span>
              </td>
              <td
                style={{
                  padding: '7px 10px',
                  borderBottom: '1px solid #f0f2f4',
                  textAlign: 'right',
                  color: m.is_up ? '#1e7d43' : '#b3271c',
                  fontWeight: 600,
                }}
              >
                {m.status}
              </td>
              {[m.uptime_24h, m.uptime_7d, m.uptime_30d].map((v, i) => (
                <td
                  key={i}
                  style={{
                    padding: '7px 10px',
                    borderBottom: '1px solid #f0f2f4',
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    color: '#5b6472',
                  }}
                >
                  {pct(v)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SystemHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    return axios
      .get(`${API}/system-health/`)
      .then((res) => {
        setData(res.data);
        setErr(null);
      })
      .catch((e) =>
        setErr(e.response?.data?.detail || 'Could not run the health checks. Try again in a moment.')
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const overall = statusOf(data?.overall_status);
  const checks = data?.checks || [];
  const certs = checks.filter((c) => c.key.startsWith('cert_'));
  const endpoints = checks.filter((c) => c.key.startsWith('endpoint_'));
  const platform = checks.filter(
    (c) => !c.key.startsWith('cert_') && !c.key.startsWith('endpoint_')
  );

  const section = (title, blurb, items) =>
    items.length > 0 && (
      <div style={{ marginBottom: '22px' }}>
        <h3 style={{ color: '#2c3e50', fontSize: '15px', margin: '0 0 2px' }}>{title}</h3>
        <p style={{ color: '#7f8c8d', fontSize: '12.5px', margin: '0 0 10px' }}>{blurb}</p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))',
            gap: '12px',
          }}
        >
          {items.map((c) => (
            <CheckCard key={c.key} check={c} />
          ))}
        </div>
      </div>
    );

  return (
    <div style={{ maxWidth: '1000px' }}>
      <h2 style={{ color: '#2c3e50', marginBottom: '4px' }}>System Health</h2>
      <p style={{ color: '#7f8c8d', fontSize: '13px', marginTop: 0 }}>
        Live checks of everything we run. Nothing here is stored or cached — each visit
        measures the real thing.
      </p>

      {err && (
        <div
          style={{
            background: '#fdecea',
            color: '#c0392b',
            padding: '8px 12px',
            borderRadius: '6px',
            marginBottom: '14px',
            fontSize: '13px',
          }}
        >
          {err}
        </div>
      )}

      {loading && !data ? (
        <div style={{ padding: '24px', color: '#999' }}>Running checks…</div>
      ) : !data ? null : (
        <>
          <div
            style={{
              ...card,
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
              background: overall.bg,
              border: `1px solid ${overall.dot}33`,
            }}
          >
            <Dot status={data.overall_status} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: overall.fg, fontSize: '15px' }}>
                {data.overall_status === 'ok'
                  ? 'Everything is healthy'
                  : data.overall_status === 'warn'
                  ? 'Something needs attention soon'
                  : data.overall_status === 'critical'
                  ? 'Something needs action now'
                  : 'Some checks could not run'}
              </div>
              <div style={{ fontSize: '12px', color: '#5b6472' }}>
                Checked {new Date(data.checked_at).toLocaleString()}
                {data.deployed_version && data.deployed_version !== 'unknown' && (
                  <> · running {String(data.deployed_version).slice(0, 7)}</>
                )}
              </div>
            </div>
            <button onClick={load} disabled={loading} style={btn(loading ? '#95a5a6' : '#2c3e50')}>
              {loading ? 'Checking…' : '↻ Re-check'}
            </button>
          </div>

          {section(
            'Certificates',
            'A certificate that stops renewing takes the site down on a date nobody is watching for.',
            certs
          )}
          {section('Reachability', 'Whether each surface answers a request right now.', endpoints)}
          {section('Platform', 'The database and the schema version the running code expects.', platform)}

          <div style={{ marginBottom: '22px' }}>
            <h3 style={{ color: '#2c3e50', fontSize: '15px', margin: '0 0 2px' }}>
              External uptime
            </h3>
            <p style={{ color: '#7f8c8d', fontSize: '12.5px', margin: '0 0 10px' }}>
              Watched from outside our own systems, so it keeps reporting even when this page
              can't. Not counted in the status above.
            </p>
            <ExternalUptime data={data.external_uptime} />
          </div>
        </>
      )}
    </div>
  );
}
